import { DataType } from '../components/DataDisplay/ValueFormatter';
import { UserFeature, UserFeatureEnum } from '../types/UserFeature';
import { StaffTaskAction } from './StaffTaskAction';
import { ColDef, Column } from '@ag-grid-community/core';
import { BoolRenderer, CurrencyRenderer, DateRenderer } from '../matterSummary/MatterSummaryCellRenderer';
import { ColumnProperty, getValueGetter } from '../components/Grid/ColumnConfig';
import moment from 'moment';
import { SummaryViewColumnConfiguration } from '../matterSummary/MatterSummaryColumns';
var menuTabs = ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'];
var nonFilterMenuTabs = ['generalMenuTab', 'columnsMenuTab'];

export function HasBudgetEditPermission(): boolean {
    return UserFeature.HasAccess(UserFeatureEnum.hasStaffBudgetCollectionsEdit) ||
    UserFeature.HasAccess(UserFeatureEnum.hasStaffBudgetBillingsEdit) ||
    UserFeature.HasAccess(UserFeatureEnum.hasStaffBudgetProductionEdit);
}

export enum cellClassEnum {
    numberType = 'numberType',
    currencyType = 'currencyType',
    stringType = 'stringType',
    dateTimeType = 'dateTimeType',
    dateType = 'dateType',
    booleanType = 'booleanType',
    actionType = 'actionType',
    imageType = 'imageType',
    currencyWithoutDecimalsType = 'currencyWithoutDecimalsType',
    percentageType = 'percentageType',
    linkType = 'linkType',
    checkboxType = 'checkboxType',
    editorViewType = 'editorViewType',
    durationType = 'durationType',
    chipsType = 'chipsType',
    contextMenuStaffType = 'contextMenuStaffType',
    dueDateType = 'dueDateType',
    timeType = 'timeType',
    ratingType = 'ratingType',
    dateMonthYear = 'dateMonthYear'
}

export function ConvertViewConfigToLayout(layoutConfiguration: string, isInMemory: boolean): Array<ColDef> {
    return ConvertColumnFromConfig(JSON.parse(layoutConfiguration), isInMemory);
}

export function ConvertViewLayoutToConfig(viewLayout: Column[]): string {
    var columnConfig = ConvertColumnsToConfig(viewLayout);
    return JSON.stringify(columnConfig);
}

function ConvertColumnsToConfig(columns: Column[]): SummaryViewColumnConfiguration[] {
    let result = new Array<SummaryViewColumnConfiguration>();
    columns.forEach(column => {
        let colDef = column.getColDef();
        let columnCfg = {
            field: colDef.field ? colDef.field : '',
            headerName: colDef.headerName ? colDef.headerName : '',
            width: column.getActualWidth(),
            dataType: getDataTypeForColumn(colDef),
            show: column.isVisible() || column.isRowGroupActive(),
            isLocked: false,
            sort: column.getSort(),
            aggFunc: colDef.aggFunc,
            enableValue: colDef.enableValue,
            valueGetter: colDef.field,
            cellClass: colDef.cellClass,
        };
        result.push(columnCfg);
    });
    return result;
}

export function GetCellRendererForColumn(column: ColDef): React.ComponentType<any> | undefined {
    if (column.field === 'action') {
        return StaffTaskAction;
    }
    return column.cellRendererFramework;
}

export function GetTooltipFieldForColumn(column: ColDef, dataType: number): string | undefined {
    if (dataType === DataType.String) {
        return column.field;
    }
    return undefined;
}

export function filterParamsOptions() {
    return {
        filterOptions: ['equals', 'notEqual', 'lessThan', 'lessThanOrEqual', 'greaterThan', 'greaterThanOrEqual', 'inRange'],
        // use inRangeInclusive: true for the range filter to include the selected 
        // from and to dates. Setting it false would fetch only the inbetween dates
        inRangeInclusive: true,  
        // tslint:disable-next-line: no-any
        comparator: (filterLocalDateAtMidnight: any, cellValue: any) => {
            var dateAsString = moment(cellValue).format('DD/MM/YYYY');
            var dateParts = dateAsString.split('/');
            var cellDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));

            if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
                return 0;
            } else if (cellDate < filterLocalDateAtMidnight) {
                return -1;
            } else if (cellDate > filterLocalDateAtMidnight) {
                return 1;
            } else {
                return -1;
            }
            
        }
    };
}

function ConvertColumnFromConfig(columnConfigurations: SummaryViewColumnConfiguration[], isInMemory: boolean): Array<ColDef> {
    let result = new Array<ColDef>();
    columnConfigurations.forEach(columncfg => {
        let filter = getFilterForDataType(columncfg.dataType);
        let showMenuTab = columncfg.dataType !== 9;
        let isDateFilter = columncfg.dataType === DataType.Date || DataType.DateTime;
        // var isActionColumn = columnConfiguration.field === 'action' || columnConfiguration.dataType === DataType.ContextMenuStaff;
        // var isLocked = columnConfiguration.isLocked || isActionColumn;
        let config = {
            field: columncfg.field,
            headerName: columncfg.headerName,
            width: columncfg.width,
            format:  columncfg.dataType,
            filter: filter ? filter : '',
            hide: !columncfg.show,
            pinned: columncfg.isLocked ? 'left' : 'none',
            menuTabs: showMenuTab ? menuTabs : nonFilterMenuTabs,
            enableRowGroup: true,
            sort: columncfg.sort,
            tooltipField: GetTooltipFieldForColumn(columncfg, columncfg.dataType),
            aggFunc: columncfg.aggFunc,
            enableValue: columncfg.enableValue,
            valueGetter: getValueGetter(columncfg.valueGetter),
            cellClass: columncfg.cellClass,
            filterParams: isDateFilter ? filterParamsOptions() : undefined,
            cellRendererFramework: GetCellRendererForColumn({ field: columncfg.field, cellRendererFramework: columncfg.cellRendererFramework } as ColDef),
        };
        applyCellRendererToColumn(config, columncfg.dataType);
        result.push(config);
    });
    return result;
}

export function DefaultColumnConfig(columns: Array<ColumnProperty> = StaffSummaryColumns): Array<ColDef> {
    let result = new Array<ColDef>();
    columns.forEach(column => {
        let showMenuTab = column.format !== DataType.ContextMenu;
        let filter = getFilterForDataType(column.format);
        let isActionColumn = column.field === 'action' || column.format === DataType.ContextMenuStaff;
        let isLocked = isActionColumn || column.lockPinned === true;
        let colDef = {
            field: column.field ? column.field : '',
            headerName: column.headerName ? column.headerName : '',
            dataType: column.format,
            width: column.width ? column.width : 50,
            format: column.format,
            filter: filter ? filter : '',
            hide: column.hide,
            show: !column.hide,
            pinned: isLocked ? 'left' : 'none',
            isLocked: isLocked,
            menuTabs: showMenuTab ? menuTabs : nonFilterMenuTabs,
            enableRowGroup: true,
            sort: column.sort ? column.sort : '',
            tooltipField: GetTooltipFieldForColumn(column, column.format),
            cellClass: column.cellClass,
            aggFunc: column.aggFunc,
            cellRendererFramework: GetCellRendererForColumn({ field: column.field, cellRendererFramework: column.cellRendererFramework } as ColDef),
        };
        applyCellRendererToColumn(colDef, column.format);
        result.push(colDef);
    });
    return result;
}

function getDataTypeForColumn(column: ColDef): number {
    if (column.cellRendererFramework === DateRenderer) { return DataType.Date; }
    if (column.cellRendererFramework === CurrencyRenderer) { return DataType.Currency; }
    if (column.cellRendererFramework === BoolRenderer) { return DataType.Boolean; }
    if (column.cellRendererFramework === StaffTaskAction) { return DataType.ContextMenuStaff; }
    return DataType.String;
}

function getFilterForDataType(dataType: number): string | undefined {
    switch (dataType) {
        case DataType.Date:
            return 'agDateColumnFilter';
        case DataType.Currency:
            return 'agNumberColumnFilter';
        case DataType.Boolean:
            return 'boolean';
        case DataType.ContextMenuStaff:
            return 'menu';
        default:
            return 'text';
    }
}

function applyCellRendererToColumn(column: ColDef, dataType: number): void {
    switch (dataType) {
        case DataType.Date:
            column.cellRendererFramework = DateRenderer;
            break;
        case DataType.Currency:
            column.cellRendererFramework = CurrencyRenderer;
            break;
        case DataType.Boolean:
            column.cellRendererFramework = BoolRenderer;
            break;
        case DataType.ContextMenuStaff:
            column.cellRendererFramework = StaffTaskAction;
            break;
        default:
            break;
    }
}
                                
export const StaffSummaryColumns: Array<ColumnProperty> = [
    {
        field: 'action',
        format: DataType.ContextMenuStaff,
        headerName: '',        
        cellStyle: { overflow: 'visible' },
        width: 70,
        // pinned: 'left',
        suppressMenu: true,
        autoHeight: true,
        resizable: false,
        // lockPosition: true,
        // lockPinned: true,
        cellRendererFramework: StaffTaskAction, // ActionCellRenderer
        cellClass: cellClassEnum.actionType
    },
    {
        headerName: 'Staff Code', 
        field: 'staffCode', 
        format: DataType.String,
        enableRowGroup: true, 
        suppressSizeToFit: true,
        width: 240,
        menuTabs: menuTabs,
        cellClass: cellClassEnum.stringType,
        hide: false
    },
    {
        headerName: 'Name', 
        field: 'name', 
        format: DataType.String,
        enableRowGroup: true, 
        suppressSizeToFit: true,
        width: 240,
        menuTabs: menuTabs,
        cellClass: cellClassEnum.stringType
    },

    {
        headerName: 'Email', 
        field: 'emailAddress', 
        format: DataType.String,
        enableRowGroup: true, 
        suppressSizeToFit: true,
        width: 240,
        menuTabs: menuTabs,
        cellClass: cellClassEnum.stringType
    },
    {
        headerName: 'Mobile', 
        field: 'mobile', 
        format: DataType.String,
        enableRowGroup: true, 
        suppressSizeToFit: true,
        width: 240,
        menuTabs: menuTabs,
        cellClass: cellClassEnum.stringType
    },
    {
        headerName: 'Phone', 
        field: 'phone', 
        format: DataType.String,
        enableRowGroup: true, 
        suppressSizeToFit: true,
        width: 240,
        menuTabs: menuTabs,
        cellClass: cellClassEnum.stringType
    },
    {
        headerName: 'Staff Classification', 
        field: 'staffRole', 
        format: DataType.String,
        enableRowGroup: true, 
        suppressSizeToFit: true,
        width: 240,
        menuTabs: menuTabs,
        cellClass: cellClassEnum.stringType,
        hide: false
    },
    {
        headerName: 'Start Date', 
        field: 'startDate', 
        format: DataType.Date,
        enableRowGroup: true, 
        suppressSizeToFit: true,
        width: 240,
        menuTabs: menuTabs,
        cellClass: cellClassEnum.dateType,
        hide: false
    },
    {
        headerName: 'End Date', 
        field: 'endDate', 
        format: DataType.Date,
        enableRowGroup: true, 
        suppressSizeToFit: true,
        width: 240,
        menuTabs: menuTabs,
        cellClass: cellClassEnum.dateType,
        hide: false
    },
    {
        headerName: 'PMS Access', 
        field: 'isLicensed', 
        format: DataType.Boolean,
        enableRowGroup: true, 
        suppressSizeToFit: true,
        width: 240,
        menuTabs: menuTabs,
        cellClass: cellClassEnum.booleanType,
        hide: false
    },
    {
        headerName: 'Office', 
        field: 'office', 
        format: DataType.String,
        enableRowGroup: true, 
        suppressSizeToFit: true,
        width: 240,
        menuTabs: menuTabs,
        cellClass: cellClassEnum.stringType,
        hide: false
    },
    {
        headerName: 'Department', 
        field: 'department', 
        format: DataType.String,
        enableRowGroup: true, 
        suppressSizeToFit: true,
        width: 240,
        menuTabs: menuTabs,
        cellClass: cellClassEnum.stringType,
        hide: false
    },
    {
        headerName: 'Team', 
        field: 'team', 
        format: DataType.String,
        enableRowGroup: true, 
        suppressSizeToFit: true,
        width: 240,
        menuTabs: menuTabs,
        cellClass: cellClassEnum.stringType,
        hide: false
    },
    {
        headerName: 'Standard Charge Rate',
        field: 'standardChargeRate',        
        format: DataType.Currency,
        suppressSizeToFit: true,
        menuTabs: menuTabs,
        enableRowGroup: true,
        aggFunc: 'sum',
        enableValue: true,
        width: 220,
        cellClass: cellClassEnum.currencyType,
    }
];

// .filter((value) => HasBudgetPermission() ? value : value.field !== 'action');